import React, {
  ForwardRefExoticComponent,
  ComponentProps,
  useState,
  createContext,
  useContext,
  ReactNode,
  useEffect,
} from 'react';
import * as RCollapsible from '@radix-ui/react-collapsible';
import styles from './CollapsibleCard.module.scss';
import { withStyles } from '../../../utils';
import SwitchIndicator from '../../Atomic/SwitchIndicator';
import Card from '../Card';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';

const cn = withStyles(styles);

type CardProps = ComponentProps<typeof Card>;

type RootProps = ComponentProps<typeof RCollapsible.Root> & {
  cardProps?: Omit<CardProps, 'children' | 'title'>;
};

const Context = createContext({
  open: false,
});

const Collapsible: ForwardRefExoticComponent<RootProps> = React.forwardRef(
  ({ className, children, cardProps, ...props }, ref) => {
    const [open, setOpen] = useState<boolean>(props.defaultOpen ?? false);

    useEffect(() => {
      if (props.open !== undefined) setOpen(props.open);
    }, [props.open]);

    return (
      <RCollapsible.Root
        ref={ref}
        {...props}
        open={open}
        onOpenChange={(val) => {
          setOpen(val);
          props.onOpenChange?.(val);
        }}
        className={cn('collapsibleContainer', className)}
        asChild
      >
        <Card
          {...cardProps}
          style={{
            padding: '0',
            display: 'block',
            ...cardProps?.style,
          }}
        >
          <Context.Provider value={{ open }}>{children}</Context.Provider>
        </Card>
      </RCollapsible.Root>
    );
  },
);

type TriggerProps = Omit<ComponentProps<typeof RCollapsible.Trigger>, 'children'> & {
  indicator?: 'none' | 'switch' | 'chevron';
  children: ReactNode | ((args: { open: boolean }) => ReactNode);
  'data-testid'?: string;
};

const Trigger: ForwardRefExoticComponent<TriggerProps> = React.forwardRef(
  ({ children, className, indicator = 'switch', 'data-testid': dataTestId, ...props }, ref) => {
    const { open } = useContext(Context);

    return (
      <RCollapsible.Trigger
        asChild
        data-testid={dataTestId || 'collapsibleTrigger'}
        ref={ref}
        {...props}
        className={cn('collapsibleTrigger', className)}
      >
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;

            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
        >
          {typeof children === 'function' ? children({ open }) : children}

          {indicator === 'switch' && <SwitchIndicator checked={open} className={cn('indicator')} />}

          {indicator === 'chevron' && (
            <div className={cn('indicator', 'chevron', open && 'open')}>
              <MaterialSymbol icon="keyboard_arrow_down" size="18px" />
            </div>
          )}
        </div>
      </RCollapsible.Trigger>
    );
  },
);

type ContentProps = ComponentProps<typeof RCollapsible.Content>;

const Content: ForwardRefExoticComponent<ContentProps> = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <RCollapsible.Content ref={ref} {...props} className={cn('collapsibleContent', className)}>
      <div className={cn('collapsibleContentInner')}>{children}</div>
    </RCollapsible.Content>
  );
});

type CompositeComponent = typeof Collapsible & {
  Trigger: typeof Trigger;
  Content: typeof Content;
};

(Collapsible as CompositeComponent).Trigger = Trigger;
(Collapsible as CompositeComponent).Content = Content;

export default Collapsible as CompositeComponent;
